import { useEffect, useRef, useState } from 'react';

import VectorIcon from 'assets/VectorIcon.svg';
import UploadIcon from 'assets/settings/components/UploadIcon';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import * as Yup from 'yup';

import { getLeadSourceImageUrl } from '../../utils/imageUtils';
import {
  ErrorText,
  ErrorText3,
  Field,
  ImagePreview,
  ImagePreviewContainer,
  ModalFormContent,
  RequiredAsterisk,
  SwitchWrapper,
  UploadButton,
  UploadContainer,
  UploadHiddenInput,
  UploadIconWrapper,
  UploadLabel,
  UploadText,
} from './LeadSourceModal.styles';

const leadSourceSchema = (isEditMode, hasOriginalImage, hasImagePreview) =>
  Yup.object({
    name: Yup.string().trim().required('Name is required'),

    description: Yup.string().max(250, 'Max 250 characters'),

    image: Yup.mixed().when([], {
      is: () => !isEditMode,
      then: (schema) =>
        schema
          .required('Image is required')
          .test('fileType', 'Only image files are allowed', (value) => {
            if (!value) return false;
            return value.type?.startsWith('image/');
          })
          .test('fileSize', 'Image must be less than 5MB', (value) => {
            if (!value) return false;
            return value.size <= 5 * 1024 * 1024;
          }),
      otherwise: (schema) =>
        schema
          .nullable()
          .test('required', 'Image is required', (value) => {
            // In edit mode, if there was an original image but now there's no preview, require new image
            if (isEditMode && hasOriginalImage && !hasImagePreview) {
              return !!value;
            }
            return true;
          })
          .test('fileType', 'Only image files are allowed', (value) => {
            if (!value) return true;
            return value.type?.startsWith('image/');
          })
          .test('fileSize', 'Image must be less than 5MB', (value) => {
            if (!value) return true;
            return value.size <= 5 * 1024 * 1024;
          }),
    }),

    enabled: Yup.boolean(),
  });
const ErrorWithIcon = ({ message }) => {
  if (!message) return null;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <img src={VectorIcon} alt="error" width={14} height={14} />
      {message}
    </span>
  );
};

const LeadSourceModal = ({
  mode = 'create',
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  initialData,
}) => {
  const isEditMode = mode === 'edit';
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
    enabled: true,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasNewImage, setHasNewImage] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);

  const hasImage = !!imagePreview || !!formData.image;
  const isUploadDisabled = isEditMode ? hasNewImage : hasImage;

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && initialData) {
      const originalUrl = initialData.image ? getLeadSourceImageUrl(initialData.image) : null;

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        image: null,
        enabled: initialData.enabled ?? true,
      });

      setImagePreview(originalUrl);
      setOriginalImageUrl(originalUrl);
      setHasNewImage(false);
    } else {
      setFormData({
        name: '',
        description: '',
        image: null,
        enabled: true,
      });
      setImagePreview(null);
      setOriginalImageUrl(null);
      setHasNewImage(false);
    }

    setErrors({});
  }, [isOpen, isEditMode, initialData]);

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSwitchToggle = () => {
    setFormData((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, image: file }));
    setHasNewImage(true);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setErrors((prev) => ({ ...prev, image: '' }));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setImagePreview(null);
    setHasNewImage(false);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    try {
      await leadSourceSchema(isEditMode, !!originalImageUrl, !!imagePreview).validate(
        formData,
        {
          abortEarly: false,
        }
      );

      const fd = new FormData();
      fd.append('name', formData.name.trim());
      fd.append('description', formData.description.trim());
      fd.append('enabled', formData.enabled);

      if (formData.image) fd.append('image', formData.image);

      if (isEditMode) {
        await onUpdate(initialData.id, fd);
      } else {
        await onCreate(fd);
      }

      onClose();
    } catch (err) {
      if (err.inner) {
        const newErrors = {};
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOk={handleSubmit}
      title={isEditMode ? 'Edit Lead Source' : 'Create Lead Source'}
      footer
      closeIcon
      okText={isEditMode ? 'Update' : 'Create'}
      cancelText="Cancel"
    >
      <ModalFormContent>
        <Field>
          <div style={{ position: 'relative' }}>
            <Input
              label="Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              required
              maxLength={50}
              error={!!errors.name}
            />
            {errors.name && (
              <ErrorText>
                <ErrorWithIcon message={errors.name} />
              </ErrorText>
            )}
          </div>
        </Field>

        <Field>
          <TextArea
            label="Description (Optional)"
            value={formData.description}
            onChange={handleInputChange('description')}
            maxLength={250}
          />
        </Field>
        <div style={{ position: 'relative' }}>
          <UploadContainer>
            <UploadLabel>
              Image {!isEditMode && <RequiredAsterisk>*</RequiredAsterisk>}
            </UploadLabel>

            <UploadHiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />

            <UploadButton
              type="button"
              onClick={handleUploadClick}
              disabled={isUploadDisabled}
              style={{
                opacity: isUploadDisabled ? 0.5 : 1,
                cursor: isUploadDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <UploadIconWrapper>
                <UploadIcon size={32} color="#296BCD" />
              </UploadIconWrapper>

              <UploadText>{hasImage ? 'Image uploaded' : 'Click to upload image'}</UploadText>
            </UploadButton>

            {/* PREVIEW */}
            {hasImage && (
              <ImagePreviewContainer
                style={{
                  position: 'relative',
                  marginTop: 12,
                  background: 'transparent',
                  padding: 0,
                  border: 'none',
                  width: 120,
                  height: 80,
                }}
              >
                <ImagePreview
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />

                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: '#fff',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 'bold',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </ImagePreviewContainer>
            )}

            {errors.image && (
              <ErrorText3>
                <ErrorWithIcon message={errors.image} />
              </ErrorText3>
            )}
          </UploadContainer>
        </div>
        {/* SWITCH */}
        <SwitchWrapper>
          <Switch isOn={formData.enabled} onToggle={handleSwitchToggle} />
          {formData.enabled ? 'Enabled' : 'Disabled'}
        </SwitchWrapper>
      </ModalFormContent>
    </Modal>
  );
};

export default LeadSourceModal;
